export const terminationContent = {
  overview: "Voxmaster's A/Z voice termination network covers 1000+ global destinations with direct Tier-1 and Tier-2 interconnects. We offer Premium routes for high-margin traffic, Standard routes for volume dialers, and a blendable LCR system so you pay for the quality tier that matches each destination.",
  stats: [
    { value: "1000+", label: "Active destinations" },
    { value: "4", label: "Route tiers (Premium / Standard / CLI / NCLI)" },
    { value: "24h", label: "Test-to-live SLA" },
    { value: "48ms", label: "Median PDD on direct routes" },
  ],
  tiers: [
    {
      name: "Premium",
      tag: "Best ASR/ACD",
      desc: "Direct Tier-1 carrier pipes with zero re-origination. Built for post-paid, high-margin, low-churn traffic where every call counts. ASR optimized to 60%+ on mobile destinations.",
      bestFor: "CC routes, retail VoIP, premium outbound dialers",
      color: "primary",
    },
    {
      name: "Standard",
      tag: "Volume optimized",
      desc: "Intelligent LCR blended routes with automated fallback. Quality-monitored hourly with automatic swap-out when ASR drops below threshold. Cost per minute is 20-40% lower than premium.",
      bestFor: "Volume carriers, traffic aggregation, dialer fallback",
      color: "accent",
    },
    {
      name: "CLI",
      tag: "Caller ID guaranteed",
      desc: "Caller-ID presentation routes where CLIR is critical for answer rates. CLI matched to destination country. Available on 120+ destinations.",
      bestFor: "B2B outbound, appointment reminder calls, survey lines",
      color: "secondary",
    },
    {
      name: "NCLI",
      tag: "Cost minimizer",
      desc: "No-CLI routes for pure volume termination at aggressive rates. Used where CLI is not required by the end operator or regulator.",
      bestFor: "SMS gateway bridging, voicemail drops, high-volume IVR",
      color: "foreground",
    },
  ],
  protocols: [
    { name: "SIP", desc: "UDP / TCP / TLS transports" },
    { name: "SRTP", desc: "Secure media encryption per session" },
    { name: "RTP", desc: "Standard RTP / RTCP" },
    { name: "WebRTC", desc: "Browser-native voice via SBC gateway" },
  ],
  codecs: [
    { name: "G.711 a/u-law", desc: "64 kbps — best for PSTN interworking" },
    { name: "G.729", desc: "8 kbps — bandwidth-optimized, no transcoding" },
    { name: "Opus", desc: "Variable — best for WebRTC and HD voice" },
    { name: "G.722", desc: "Wideband — HD voice at 48-64 kbps" },
    { name: "iLBC", desc: "15.2 kbps — resilient to packet loss" },
    { name: "Speex", desc: "Variable — legacy VoIP softswitch" },
  ],
  routingFeatures: [
    "Real-time LCR engine with profit-margin overrides",
    "Destination-level route selection (per prefix)",
    "Automatic failover with 5-second hold-and-retry",
    "Multiple account hierarchy for sub-carriers and resellers",
    "Real-time ASR/ACD/PDD alerting per destination",
    "CLI validation and forced re-dial on CLIR drop",
  ],
};

export const didContent = {
  overview: "Voxmaster delivers local, national, mobile and toll-free DID numbers across 60+ countries with instant provisioning, elastic porting, and a REST API for inventory management. Whether you need a single test number or 50,000 in a batch, the platform scales with your application.",
  stats: [
    { value: "60+", label: "Countries available" },
    { value: "500k+", label: "Active numbers" },
    { value: "2min", label: "Average provisioning time" },
    { value: "4hr", label: "Porting SLA" },
  ],
  types: [
    {
      name: "Local Numbers",
      icon: "ri-map-pin-2-line",
      desc: "Geographic local DIDs in city/area codes across 60+ countries. Used for local presence and local call routing.",
      coverage: "48 countries",
    },
    {
      name: "National Numbers",
      icon: "ri-building-4-line",
      desc: "Non-geographic national numbers with nationwide accessibility. Ideal for unified corporate lines.",
      coverage: "22 countries",
    },
    {
      name: "Mobile Numbers",
      icon: "ri-smartphone-line",
      desc: "Mobile-originated DIDs in markets where mobile numbers are preferred over fixed-line origination.",
      coverage: "18 countries",
    },
    {
      name: "Toll-Free",
      icon: "ri-customer-service-2-line",
      desc: "Freephone numbers in the format 800, 888, 1800 per local convention. Inbound routing with multi-SBC failover.",
      coverage: "35 countries",
    },
  ],
  features: [
    {
      title: "Instant provisioning",
      desc: "Reserve numbers in the portal and assign them to your SIP trunk within two minutes. API-driven for programmatic provisioning.",
      icon: "ri-flashlight-line",
    },
    {
      title: "Port-in support",
      desc: "Bulk number porting with automated LOA generation. Porting SLA of four hours for active carriers, two business days for standard.",
      icon: "ri-swap-line",
    },
    {
      title: "Elastic scaling",
      desc: "Provision 10 numbers today, 10,000 tomorrow. Elastic billing means you pay per active number, not per reserved block.",
      icon: "ri-expand-diagonal-line",
    },
    {
      title: "REST API",
      desc: "Full API coverage: inventory search, number reservation, CDR pull, billing query, and porting status tracking.",
      icon: "ri-code-s-slash-line",
    },
    {
      title: "Fax-to-email",
      desc: "T.38-compliant inbound fax routing with automatic PDF attachment delivery to any email address.",
      icon: "ri-mail-send-line",
    },
    {
      title: "E911 support",
      desc: "Regulatory-compliant emergency routing for US and Canada DIDs with real-time address database sync.",
      icon: "ri-first-aid-kit-line",
    },
  ],
  popularDestinations: [
    { country: "United States", format: "+1", types: "Local, Toll-Free, Mobile", leadTime: "Instant" },
    { country: "United Kingdom", format: "+44", types: "Local, National", leadTime: "Instant" },
    { country: "Germany", format: "+49", types: "Local, National", leadTime: "Instant" },
    { country: "Australia", format: "+61", types: "Local, Mobile", leadTime: "Instant" },
    { country: "Canada", format: "+1", types: "Local, Toll-Free", leadTime: "Instant" },
    { country: "Mexico", format: "+52", types: "Local, Mobile", leadTime: "Instant" },
    { country: "Brazil", format: "+55", types: "Local, Mobile", leadTime: "Instant" },
    { country: "UAE", format: "+971", types: "Local, Mobile", leadTime: "4h" },
    { country: "South Africa", format: "+27", types: "Local, Mobile", leadTime: "Instant" },
    { country: "India", format: "+91", types: "Local, Mobile, Toll-Free", leadTime: "Instant" },
  ],
};

export const sipTrunkingContent = {
  overview: "Voxmaster's SIP Trunking service delivers elastic, secure voice channels to your PBX, CPaaS platform, or softswitch. No channel lock-in — scale up and down based on real traffic, not a flat monthly minimum.",
  stats: [
    { value: "∞", label: "Elastic channel scaling" },
    { value: "99.99%", label: "Platform uptime SLA" },
    { value: "<3s", label: "Failover switchover" },
    { value: "15+", label: "PBX integrations certified" },
  ],
  features: [
    {
      title: "Elastic call channels",
      desc: "Pay for what you use. Scale from 10 to 10,000 concurrent calls without re-provisioning or renegotiating contracts.",
      icon: "ri-bar-chart-grouped-line",
    },
    {
      title: "Multi-SBC redundancy",
      desc: "Four geographically distributed SBCs with automatic least-latency routing. If one region drops, your calls reroute in under three seconds.",
      icon: "ri-server-line",
    },
    {
      title: "IP authentication",
      desc: "Registerless trunking via static IP white-listing — no credentials to leak, no SIP registration load on your switch.",
      icon: "ri-shield-keyhole-line",
    },
    {
      title: "Codec negotiation",
      desc: "Auto-transcoding between your PBX codecs and our carrier network. No need to force G.711 on every trunk.",
      icon: "ri-sound-module-line",
    },
    {
      title: "DID pooling",
      desc: "Attach unlimited DIDs to a single SIP trunk for inbound routing. Numbers share capacity so you never buy spare channels per DID.",
      icon: "ri-stack-line",
    },
    {
      title: "WebRTC gateway",
      desc: "Browser-based voice sessions via WebRTC-SIP bridge. Ideal for browser dialers, click-to-call, and softphone portals.",
      icon: "ri-global-line",
    },
  ],
  pbxList: [
    "Cisco Unified Communications",
    "Avaya Aura / IP Office",
    "3CX",
    "Asterisk / FreePBX",
    "FreeSWITCH",
    "Kamailio",
    "Grandstream UCM",
    "Yeastar",
    "Mitel",
    "Sangoma Switchvox",
    "Alcatel-Lucent OXO/OXE",
    "Siemens OpenScape",
    "Freeswitch",
    "Twilio Elastic SIP Trunk (migration)",
    "Vonage Business (migration)",
  ],
  security: [
    { title: "TLS transport", desc: "SIP signaling over TLS 1.3 with mutual certificate authentication." },
    { title: "SRTP encryption", desc: "AES-256 media encryption per call. Optional ZRTP for end-to-end." },
    { title: "Static IP only", desc: "No SIP registration by credential. Only pre-approved IP ranges may reach our SBC." },
    { title: "Rate limiting", desc: "Per-IP CPS caps and burst-rate throttling to prevent overload and fraud." },
    { title: "SBC firewall", desc: "Application-layer firewall on every SBC: malformed SIP, botnet patterns, and reflection attack filtering." },
  ],
};

export const resellersContent = {
  overview: "Launch a VoIP resale business under your own brand. We handle the carrier infrastructure, you keep the customer relationships and margins.",
  features: [
    {
      title: "White-label rate decks",
      desc: "Export custom rate decks under your own branding with your margins built in.",
      icon: "ri-file-copy-line",
    },
    {
      title: "Sub-account management",
      desc: "Create unlimited sub-accounts for your customers with individual routing and billing profiles.",
      icon: "ri-user-add-line",
    },
    {
      title: "Margin-friendly pricing",
      desc: "Wholesale rates designed to leave healthy room for resale markup.",
      icon: "ri-price-tag-3-line",
    },
    {
      title: "Co-branded portal",
      desc: "White-labeled customer portal for balance, CDRs, and DID management.",
      icon: "ri-window-2-line",
    },
    {
      title: "Instant DID provisioning",
      desc: "Resell DID numbers from our 60+ country inventory with instant activation.",
      icon: "ri-phone-line",
    },
    {
      title: "Volume incentives",
      desc: "Aggregated volumes count toward tiered pricing — the more your customers grow, the better your rates.",
      icon: "ri-bar-chart-grouped-line",
    },
  ],
  stats: [
    { value: "100%", label: "Your brand, your customers" },
    { value: "60+", label: "Countries for DID resale" },
    { value: "4", label: "White-label portal themes" },
    { value: "24h", label: "Reseller onboarding SLA" },
  ],
};

export const operatorsContent = {
  overview: "Carrier-grade interconnects for VoIP operators routing millions of minutes per month. Private peering, volume terms, and proactive NOC.",
  features: [
    {
      title: "Private interconnects",
      desc: "Dedicated VLANs and private SBC peering for maximum security and lowest latency.",
      icon: "ri-shield-keyhole-line",
    },
    {
      title: "Volume-based terms",
      desc: "Tiered per-minute rates that improve as your traffic volume grows.",
      icon: "ri-line-chart-line",
    },
    {
      title: "N+1 SBC redundancy",
      desc: "Geographically separated SBCs with automatic failover under 5 seconds.",
      icon: "ri-server-line",
    },
    {
      title: "Unified CDR and billing API",
      desc: "Single REST endpoint for CDRs, disputes, and real-time balance.",
      icon: "ri-file-list-line",
    },
    {
      title: "Route testing",
      desc: "Hourly test calls across active destinations with proactive route swap suggestions.",
      icon: "ri-test-tube-line",
    },
    {
      title: "Regulatory guidance",
      desc: "Stay ahead of CLI validation, emergency routing, and compliance changes.",
      icon: "ri-file-list-3-line",
    },
  ],
  stats: [
    { value: "48h", label: "From NDA to first test call" },
    { value: "4", label: "Active SBC regions" },
    { value: "700+", label: "Interconnect partners" },
    { value: "99.97%", label: "SLA on direct links" },
  ],
};

export const callCentersContent = {
  overview: "Routes and APIs purpose-built for outbound dialers, contact centers, and programmable voice platforms.",
  features: [
    {
      title: "Dialer-optimized routes",
      desc: "Low PDD, high ASR routes tuned for predictive and power dialers.",
      icon: "ri-phone-line",
    },
    {
      title: "Per-campaign monitoring",
      desc: "Track route performance per campaign ID with live swap capability.",
      icon: "ri-line-chart-line",
    },
    {
      title: "Outbound CLI control",
      desc: "Local CLI presentation with rotation and geographic matching.",
      icon: "ri-fingerprint-line",
    },
    {
      title: "CPaaS REST API",
      desc: "Programmable voice with webhook callbacks for call status and recordings.",
      icon: "ri-code-s-slash-line",
    },
    {
      title: "Concurrent call scaling",
      desc: "Burst from hundreds to tens of thousands of concurrent calls.",
      icon: "ri-flashlight-line",
    },
    {
      title: "Compliance tooling",
      desc: "Timezone dial-window, DNC scrubbing, and recording consent flows.",
      icon: "ri-shield-check-line",
    },
  ],
  stats: [
    { value: "<38ms", label: "Median PDD on dialer routes" },
    { value: "60%+", label: "Target ASR on mobile routes" },
    { value: "∞", label: "Concurrent call scaling" },
    { value: "100%", label: "CLI presentation support" },
  ],
};

export const antiFraudContent = {
  overview: "Multi-layer fraud protection: CLI validation, velocity detection, revenue-share blocking, and 24/7 fraud NOC.",
  features: [
    {
      title: "CLI validation engine",
      desc: "Real-time CLI whitelist checks reject spoofed or manipulated CLIs before the call leaves the SBC.",
      icon: "ri-fingerprint-line",
    },
    {
      title: "Velocity-based detection",
      desc: "Monitor call patterns per destination, account, and IP with automatic alert triggers.",
      icon: "ri-speed-line",
    },
    {
      title: "Revenue-share blocking",
      desc: "Daily-updated blacklists of known fraud destinations blocked at the routing layer.",
      icon: "ri-shield-check-line",
    },
    {
      title: "ASR threshold routing",
      desc: "Routes with collapsed ASR are automatically removed from LCR — a key fraud indicator.",
      icon: "ri-bar-chart-grouped-line",
    },
    {
      title: "Account rate limiting",
      desc: "Per-account CPS caps, daily spend limits, and burst throttles for credential protection.",
      icon: "ri-lock-2-line",
    },
    {
      title: "24/7 fraud NOC",
      desc: "Dedicated anti-fraud team monitoring global abuse patterns and pushing protective rules fleet-wide.",
      icon: "ri-customer-service-2-line",
    },
  ],
  stats: [
    { value: "<0.1%", label: "Fraud rate across all traffic" },
    { value: "24/7", label: "Dedicated fraud NOC" },
    { value: "100%", label: "CLI validation coverage" },
    { value: "5min", label: "Threat response time" },
  ],
};